import {
  ExistingTaxIdentificationNumber,
  InvalidTaxIdentificationNumber,
  TaxIdentificationNumber,
  ValidTaxIdentificationNumber,
} from 'Agent/Policies/models/TaxIdentificationNumber';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {Errors} from 'Shared/components/ErrorPopover';
import ValidatableTextField from 'Shared/components/ValidatableTextField';
import {maskTaxId} from 'Shared/utilities/taxIdMasker';
import {changeAgentNumbers, fetchAgentNumbersByTaxIdentifier} from 'Users/ducks/agentNumber';
import {changeIsTaxIdentifierValid, validateTaxIdentifier} from 'Users/ducks/validate';

export type TaxIdNumberInputProps = {
  value: TaxIdentificationNumber;
  onChange: (value: TaxIdentificationNumber) => void;
  clearAgentNumbers: () => void;
  allTaxIdentifiers: TaxIdentificationNumber[];
  uniqueKey: string;
  encryptedIdentifier: string;
  disabled: boolean;
};

const TaxIdNumberInput = (props: TaxIdNumberInputProps) => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState(Errors.standardErrors({value: []}));
  const isTaxIdentifierValid = useSelector(
    (state: ReducerState) => state.user.validate.isTaxIdentifierValidDict[props.uniqueKey]
  );

  const taxIdValue =
    props.value instanceof ExistingTaxIdentificationNumber ? props.value.maskedIdentifier : props.value.value;

  const changeTaxIdentifier = (value: string) => {
    const newValue = ValidTaxIdentificationNumber.create(value, props.allTaxIdentifiers);

    if (newValue instanceof ValidTaxIdentificationNumber) {
      dispatch(validateTaxIdentifier(newValue.value, props.uniqueKey));
    }

    if (newValue instanceof InvalidTaxIdentificationNumber ) {
      dispatch(changeIsTaxIdentifierValid(false, props.uniqueKey));
    }

    props.onChange(newValue);
  };

  const validation = () => {
    if (props.value instanceof InvalidTaxIdentificationNumber) {
      setErrors(
        Errors.standardErrors({
          value: [props.value.error],
        })
      );
    }
  };

  useEffect(() => {
    if (props.encryptedIdentifier) {
      dispatch(fetchAgentNumbersByTaxIdentifier(props.encryptedIdentifier, props.uniqueKey, props.encryptedIdentifier));
    }

    if (props.value instanceof ExistingTaxIdentificationNumber) {
      dispatch(fetchAgentNumbersByTaxIdentifier(props.value.value, props.uniqueKey, props.value.value));
    }

    if (props.disabled) {
      return;
    }

    if (isTaxIdentifierValid) {
      setErrors(
        Errors.standardErrors({
          value: [],
        })
      );
      dispatch(fetchAgentNumbersByTaxIdentifier(props.value.value, props.uniqueKey, props.encryptedIdentifier));
    } else {
      validation();
      dispatch(changeAgentNumbers([], props.uniqueKey));
      props.clearAgentNumbers();
    }
  }, [isTaxIdentifierValid]);

  return (
    (!props.encryptedIdentifier && (
      <ValidatableTextField
        placeholder="XXX-XX-XXXX"
        label="Tax ID Number"
        errors={errors}
        showErrors={() => !isTaxIdentifierValid && !!errors.value[0]}
        disabled={props.disabled}
        inputProps={{
          maxLength: 11,
        }}
        onBlur={validation}
        className="agent-entry__field"
        onChange={changeTaxIdentifier}
        value={maskTaxId(taxIdValue)}
      />
    )) || <></>
  );
};

export default TaxIdNumberInput;
